/**
 * Returns an array of numbers from start to stop with a step size of step.
 */
export const arrayRange = (start: number, stop: number, step: number) =>
    Array.from({length: (stop - start) / step + 1}, (value, index) => start + index * step);

/**
 * Given an obj, returns a new object with the same keys and values, but with
 * all keys and values trimmed. If a value is not a string, nothing is done to
 * the value.
 *
 * @return a new object with all keys and string values trimmed
 */
export const trimKeysAndValuesInObject = <T>(obj: Record<string, T>): Record<string, T> => {
    const trimmedObject: Record<string, any> = {};
    Object.keys(obj).forEach(key => {
        const trimmedKey = key.trim();
        const value = obj[key];
        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        trimmedObject[trimmedKey] = trimmedValue;
    });
    return trimmedObject;
};

/**
 * Sorts an object by its keys.
 *
 * Note that this relies on the insertion order resulting in consistent
 * iteration order. There may be some caveats when number-like strings are
 * used as keys.
 *
 * @return New shallow copy object with keys sorted alphabetically
 */
export const sortObjectKeys = <T>(obj: Record<string, T>): Record<string, T> => {
    const keys = Object.keys(obj).sort();
    const sortedObj: Record<string, T> = {};
    keys.forEach(key => {
        sortedObj[key] = obj[key];
    });
    return sortedObj;
};

/**
 * Capitalizes the first letter of a string.
 * @param str - the string to capitalize
 * @return the string with the first letter capitalized, empty string if other type
 */
export const capitalize = (str: string): string => {
    try {
        return str.charAt(0).toUpperCase() + str.slice(1);
    } catch (e) {
        return '';
    }
};

/**
 * Given a file object and some metadata, returns an object that can be sent to
 * the bff for file creation either as part of sample/create or standalone at
 * file/create (if sample_id is given).
 */
export const fileToBffEntry = (f: File, seq_sense: string, seq_read_type: string, sample_id?: string) => {
    // Names follow a specific format that includes lane and read
    // number separated by underscores.
    // TODO This part does feel a bit brittle.
    const filename = f.name;
    const filenameSplit = filename.split('_');
    const laneNumber = filenameSplit.length >= 5 ? filenameSplit[filenameSplit.length - 3][3] : '1';
    const readNumber =
        filenameSplit.length >= 5 ? filenameSplit[filenameSplit.length - 2][1] : filenameSplit.length === 2 ? filenameSplit[1][0] : '1';
    const result = {
        name: filename,
        original_size: f.size,
        strandedness_seq_sense: seq_sense,
        strandedness_seq_end: seq_read_type,
        interleaved: false,
        status: 'unknown',
        lane_number: laneNumber,
        read_number: readNumber,
        sample_id: sample_id
    };
    return result;
};

/**
 * Contacts the bff to create a new RawFile entry in the database
 * associated with the current sample.
 *
 * The function signature's return type is the minimal we need when using this
 * function. The actual object will have other properties too.
 */
export const addFileToSample = async (
    apiHost: string,
    apiAccessToken: string,
    experiment_id: string,
    sample_id: string,
    fileObject: any
): Promise<{id: string; name: string}> => {
    const response = await fetch(`${apiHost}/experiment/${experiment_id}/sample/${sample_id}/file/create`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${apiAccessToken}`
        },
        body: JSON.stringify(fileObject)
    });
    return await response.json();
};
